import { Form, Select } from "antd";
import type { FormAlternativeUI } from "../utils";
import {
  ActivityMainDevelopmentDomain,
  ActivityInteractionType,
  ActivityTypeOfEducationalActivity,
  ActivityBloomLevel,
  ActivityPrimariaInteractionType,
  ActivityEvidenceType,
  ActivityNature,
  resolveActivityMainDevelopmentDomain,
  resolveActivityInteractionType,
  resolveActivityTypeOfEducationalActivity,
  resolveActivityBloomLevel,
  resolveActivityPrimariaInteractionType,
  resolveActivityEvidenceType,
  resolveActivityNature,
} from "../utils";
import { useLessonForm } from "./LessonFormContext";
import CustomSelect from "../../../common/custom-select";

interface AlternativePillsProps {
  activityIndex: number;
  alternativeIndex: number;
  alternative: FormAlternativeUI;
}

/**
 * SCRUM-795: las pastillas pedagógicas de una alternativa sin manipulativos.
 *
 * No reusa `PedagogicalPills` a propósito: ese componente guarda sus valores en
 * el estado paralelo `pillSuggestions`, indexado por actividad, para poder
 * ofrecer sugerencias de IA sobre la actividad. Una alternativa guarda sus
 * pastillas en su propio objeto, así que estirar ese índice para cubrir
 * "actividad + alternativa" agregaría una dimensión al estado de IA sin que
 * nadie la use.
 */
const AlternativePills: React.FC<AlternativePillsProps> = ({
  activityIndex,
  alternativeIndex,
  alternative,
}) => {
  const { educationLevel, handleAlternativeUpdate } = useLessonForm();
  const isPrimaria = educationLevel === "PRIMARIA";

  const update = <K extends keyof FormAlternativeUI>(
    key: K,
    value: FormAlternativeUI[K],
  ) => handleAlternativeUpdate(activityIndex, alternativeIndex, key, value);

  const rowStyle = {
    display: "flex",
    gap: 16,
    alignItems: "flex-start",
    marginBottom: 16,
  } as const;

  if (isPrimaria) {
    const nature = alternative.activity_nature || [];

    return (
      <div className="activity-pills-row" style={rowStyle}>
        <div style={{ flex: 1 }}>
          <Form.Item label="Nivel cognitivo (Bloom)">
            <Select
              value={alternative.bloom_level?.[0]}
              placeholder="Selecciona nivel Bloom"
              onChange={(value: ActivityBloomLevel) =>
                update("bloom_level", [value])
              }
              options={Object.values(ActivityBloomLevel).map((value) => ({
                value,
                label: resolveActivityBloomLevel(value),
              }))}
            />
          </Form.Item>
        </div>
        <div style={{ flex: 1 }}>
          <Form.Item label="Tipo de interacción">
            <Select
              value={alternative.primaria_interaction_type?.[0]}
              placeholder="Selecciona tipo de interacción"
              onChange={(value: ActivityPrimariaInteractionType) =>
                update("primaria_interaction_type", [value])
              }
              options={Object.values(ActivityPrimariaInteractionType).map(
                (value) => ({
                  value,
                  label: resolveActivityPrimariaInteractionType(value),
                }),
              )}
            />
          </Form.Item>
        </div>
        <div style={{ flex: 1 }}>
          <Form.Item label="Tipo de evidencia observable">
            <Select
              mode="multiple"
              maxTagCount={2}
              maxTagTextLength={10}
              value={alternative.evidence_type || []}
              placeholder="Selecciona tipo de evidencia"
              onChange={(value: ActivityEvidenceType[]) =>
                update("evidence_type", value.slice(0, 2))
              }
              options={Object.values(ActivityEvidenceType).map((value) => ({
                value,
                label: resolveActivityEvidenceType(value),
              }))}
            />
          </Form.Item>
        </div>
        <div style={{ flex: 1 }}>
          <Form.Item label="Naturaleza de la actividad">
            <Select
              mode="multiple"
              maxTagCount={2}
              maxTagTextLength={12}
              value={nature}
              placeholder="Selecciona naturaleza"
              onChange={(value: ActivityNature[]) =>
                update("activity_nature", value.slice(0, 2))
              }
              options={Object.values(ActivityNature).map((value) => ({
                value,
                label: resolveActivityNature(value),
              }))}
            />
          </Form.Item>
        </div>
      </div>
    );
  }

  return (
    <div className="activity-pills-row" style={rowStyle}>
      <div style={{ flex: 1 }}>
        <CustomSelect
          label="Dominio de desarrollo principal"
          placeholder="Selecciona un dominio de desarrollo"
          options={Object.values(ActivityMainDevelopmentDomain).map(
            (value) => ({
              value,
              label: resolveActivityMainDevelopmentDomain(value),
            }),
          )}
          value={alternative.mainDevelopmentDomains || []}
          onChange={(value: ActivityMainDevelopmentDomain[]) =>
            update("mainDevelopmentDomains", value)
          }
          className="activity-development-domain"
        />
      </div>
      <div style={{ flex: 1 }}>
        <CustomSelect
          label="Tipo de interacción"
          placeholder="Selecciona un tipo de interacción"
          options={Object.values(ActivityInteractionType).map((value) => ({
            value,
            label: resolveActivityInteractionType(value),
          }))}
          value={alternative.interactionTypes || []}
          onChange={(value: ActivityInteractionType[]) =>
            update("interactionTypes", value)
          }
          className="activity-interaction-type"
        />
      </div>
      <div style={{ flex: 1 }}>
        <CustomSelect
          label="Tipo de actividad didáctica preescolar"
          placeholder="Selecciona un tipo de actividad"
          options={Object.values(ActivityTypeOfEducationalActivity).map(
            (value) => ({
              value,
              label: resolveActivityTypeOfEducationalActivity(value),
            }),
          )}
          value={alternative.typesOfEducationalActivities || []}
          onChange={(value: ActivityTypeOfEducationalActivity[]) =>
            update("typesOfEducationalActivities", value)
          }
          className="activity-type-of-educational-activity"
        />
      </div>
    </div>
  );
};

export default AlternativePills;
